import AuthService, { type User, type UserRole, type LoginResponse } from '../services/AuthService';
import { defineStore } from 'pinia';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  expiresAt: number | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => {
    // Clear any potentially corrupted data (temporary debug)
    if (typeof window !== 'undefined') {
      const expires = localStorage.getItem('ecomac_expires');
      if (expires === 'undefined' || expires === 'null') {
        localStorage.removeItem('ecomac_expires');
        localStorage.removeItem('ecomac_token');
        localStorage.removeItem('ecomac_user');
      }
    }
    
    try {
      return {
        user: JSON.parse(localStorage.getItem('ecomac_user') || 'null'),
        token: localStorage.getItem('ecomac_token'),
        isLoading: false,
        error: null,
        expiresAt: parseInt(localStorage.getItem('ecomac_expires') || '0'),
      };
    } catch (error) {
      // If there's an error parsing stored data, return clean state
      return {
        user: null,
        token: null,
        isLoading: false,
        error: null,
        expiresAt: null,
      };
    }
  },
  
  getters: {
    isAuthenticated: (state) => {
      if (!state.token || !state.user) return false;
      if (state.expiresAt && Date.now() > state.expiresAt) {
        return false;
      }
      return true;
    },
    
    userRole: (state): UserRole | null => state.user?.role || null,
    
    isClient: (state) => state.user?.role === 'client',
    isLeader: (state) => state.user?.role === 'leader',
    isAdmin: (state) => state.user?.role === 'admin',
    
    canViewAllTickets: (state) => {
      const role = state.user?.role;
      return role === 'admin' || role === 'leader';
    },
    
    userName: (state) => state.user?.name || 'Usuario',
    userEmail: (state) => state.user?.email || '',
  },
  
  actions: {
    async login(email: string, password: string): Promise<void> {
      this.isLoading = true;
      this.error = null;
      
      try {
        const response: LoginResponse = await AuthService.login(email, password);
        
        console.log('Login response:', response);
        console.log('ExpiresAt value:', response.expiresAt, 'Type:', typeof response.expiresAt);
        
        this.user = response.user;
        this.token = response.token;
        this.expiresAt = response.expiresAt;
        
        // Store in localStorage with Ecomac prefix
        localStorage.setItem('ecomac_user', JSON.stringify(response.user));
        localStorage.setItem('ecomac_token', response.token);
        const expiresValue = response.expiresAt || Date.now() + 24*60*60*1000;
        console.log('Storing expires value:', expiresValue);
        localStorage.setItem('ecomac_expires', expiresValue.toString());
        
      } catch (error: any) {
        this.error = error.message || 'Error al iniciar sesión';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    
    async logout(): Promise<void> {
      this.isLoading = true;
      
      try {
        if (this.token) {
          await AuthService.logout(this.token);
        }
      } catch (error) {
        console.warn('Error during logout:', error);
      } finally {
        this.clearSession();
        this.isLoading = false;
      }
    },
    
    clearSession() {
      this.user = null;
      this.token = null;
      this.expiresAt = null;
      this.error = null;
      
      // Clear localStorage
      localStorage.removeItem('ecomac_user');
      localStorage.removeItem('ecomac_token');
      localStorage.removeItem('ecomac_expires');
    },
    
    async validateSession(): Promise<boolean> {
      if (!this.token) return false;
      
      try {
        const isValid = await AuthService.validateToken(this.token);
        if (!isValid) {
          this.clearSession();
          return false;
        }
        return true;
      } catch (error) {
        this.clearSession();
        return false;
      }
    },
    
    async autoLogin(): Promise<boolean> {
      const user = JSON.parse(localStorage.getItem('ecomac_user') || 'null');
      const token = localStorage.getItem('ecomac_token');
      const expires = parseInt(localStorage.getItem('ecomac_expires') || '0');
      
      if (!user || !token || !expires) {
        this.clearSession();
        return false;
      }
      
      // Check if token is expired
      if (Date.now() > expires) {
        this.clearSession();
        return false;
      }
      
      this.user = user;
      this.token = token;
      this.expiresAt = expires;
      
      // Skip server validation for now to avoid circular dependency
      return true;
    },
    
    clearError() {
      this.error = null;
    },
    
    hasRole(requiredRole: UserRole): boolean {
      if (!this.token) return false;
      return AuthService.hasRole(this.token, requiredRole);
    },
  },
});

